import { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import useLocalStorageContextHook from '@/hooks/useLocalStorageContextHook';
import dayjs from 'dayjs';
import { getAnalyticsUserData } from './getAnalyticsProps';

export default function usePlausibleAnalytics() {
  const router = useRouter();
  const firstPageviewSentRef = useRef(false);
  const globalContextStateRef = useRef({});
  const studentContextStateRef = useRef({});

  const { state, actions } = useLocalStorageContextHook({
    contextName: 'plausibleAnalytics',
    defaultState: {
      loaded: false,
      sessionId: null,
      userId: null,
    },
    actions: {
      sendPlausibleEvent: (eventName, props = {}) => {
        if (
          !globalContextStateRef.current?.loaded ||
          !studentContextStateRef.current?.loaded ||
          !state.sessionId ||
          !state.userId
        ) {
          return;
        }
        const userData = getAnalyticsUserData(
          globalContextStateRef.current,
          studentContextStateRef.current
        );
        const eventProps = {
          sessionId: state.sessionId,
          userId: state.userId,
          ...userData,
          ...props,
        };

        plausible(eventName, {
          props: eventProps,
        });
      },
      setGlobalContextState: (globalContextState) => {
        globalContextStateRef.current = { ...globalContextState };
      },
      setStudentContextState: (studentContextState) => {
        studentContextStateRef.current = { ...studentContextState };
      },
    },
  });

  useEffect(() => {
    if (state.loaded) {
      const now = dayjs();
      const sessionTime = state.sessionId
        ? dayjs(state.sessionId.split('_')[0], 'YYYYMMDDHHmmss')
        : null;

      const newState = {};
      if (!state.sessionId || now.diff(sessionTime, 'hour') > 0) {
        const random = generateRandomString();
        newState.sessionId = now.format('YYYYMMDDHHmmss_') + random;
      }
      if (!state.userId) {
        newState.userId = generateRandomString();
      }
      if (Object.keys(newState).length > 0) {
        actions.setState(newState);
      }
    }
  }, [state.loaded]);

  useEffect(() => {
    function sendPageview() {
      actions.sendPlausibleEvent('pageview');
    }
    if (
      globalContextStateRef.current?.loaded &&
      studentContextStateRef.current?.loaded &&
      (!globalContextStateRef.current?.user?.student ||
        studentContextStateRef.current?.subscription?.id) &&
      state.sessionId &&
      state.userId &&
      !firstPageviewSentRef.current
    ) {
      firstPageviewSentRef.current = true;
      sendPageview();
    }

    router.events.on('routeChangeComplete', sendPageview);
    return () => {
      router.events.off('routeChangeComplete', sendPageview);
    };
  }, [
    globalContextStateRef.current?.loaded,
    globalContextStateRef.current?.user,
    studentContextStateRef.current?.loaded,
    studentContextStateRef.current?.subscription?.id,
    state.sessionId,
    state.userId,
    router.events,
  ]);

  useEffect(() => {
    if (
      globalContextStateRef.current?.loaded &&
      globalContextStateRef.current?.user
    ) {
      if (
        !globalContextStateRef.current?.user?.student ||
        studentContextStateRef.current?.subscription?.id
      ) {
        actions.sendPlausibleEvent('session_start');
      }
    }
  }, [
    globalContextStateRef.current?.loaded,
    globalContextStateRef.current?.user?.student?.id,
    globalContextStateRef.current?.user?.teacher?.id,
    studentContextStateRef.current?.loaded,
    studentContextStateRef.current?.subscription?.id,
  ]);

  return { state, actions };
}

function generateRandomString() {
  try {
    let uuid = null;
    if (crypto?.randomUUID) {
      uuid = crypto.randomUUID();
    }
    return uuid || Math.random().toString(36).substring(2, 9);
  } catch (error) {
    return Math.random().toString(36).substring(2, 9);
  }
}
